import { useCallback, useEffect, useRef, useState } from "react";
import moment from "moment-timezone";
import { toast } from "react-toastify";
import {
  capturePanelFailure,
  panelLoadBreadcrumb,
} from "../../config/capturePanelFailure";
import {
  exportClientIotEventsCsv,
  fetchClientIotEvents,
} from "../../api/iotEventsService";
import {
  clientDayEndIso,
  clientDayStartIso,
  formatClientZonedTime,
  resolveClientDisplayZone,
} from "../../utils/activityTimezone";

/**
 * GMA-1400 — vendor-agnostic "IoT Alert History" section for the client profile.
 *
 * Durable view of a client's IoT alerts (acknowledged AND unacknowledged) with
 * date-range / severity / type / acknowledged filters, plus a CSV export for
 * QA / SOP2 audit. Mirrors the staff-section pattern (PontosenseStaffSection /
 * XandarKardianSection). Access is enforced server-side (GMA-710 ACL).
 */
/**
 * Info is deliberately NOT offered. The two severities a caregiver audits are
 * Critical and Warning, and an "Info" choice sent them to a page of presence
 * transitions and unclassified events.
 *
 * ⚠ This removes the OPTION, never the rows. `iot_events.severity` DEFAULTS to
 * "info" (cortex-be models/IotEvent.js) and the vendor adapters return "info"
 * for every event type they do not classify (adapters/iotVendorAdapters.js), so
 * an info row is often an UNRECOGNISED sensor event — precisely what an auditor
 * is here to find, and what the delivery banner below already warns can go
 * missing. "All severities" therefore still lists and still exports them, and
 * SEVERITY_BADGE_COLORS keeps its `info` entry so those rows stay legible.
 * Guarded by IotEventHistorySeverityOptions.test.jsx.
 */
const SEVERITY_OPTIONS = [
  { value: "", label: "All severities" },
  { value: "critical", label: "Critical" },
  { value: "warning", label: "Warning" },
];

/**
 * #511 — the Source column (#510) made vendor visible; this makes it
 * selectable.
 *
 * The list is STATIC on purpose. Deriving it from the rows currently loaded
 * would make the control a one-way door: filter to one vendor and every other
 * option vanishes along with the rows that produced it, leaving no way back
 * except a page reload.
 *
 * VALUES are the machine `name`, because the backend resolves the filter with
 * `IotVendor.findOne({ where: { name } })` — the display label would not match.
 * LABELS are the seeded `display_name`, so an option reads identically to the
 * Source cell on the rows it selects.
 *
 * ⚠ `hydrostasis` is deliberately ABSENT and must not be added. It exists in
 * the backend code registry and has an adapter, but has NO row in
 * `iot_vendors`, and the backend vendor filter FAILS OPEN — an unresolved
 * vendor name adds no constraint and returns every vendor's rows with a 200.
 * Offering it would silently show the user everything while the control
 * claimed to show one vendor. Tracked as GMA-TECH/cortex-be#1039; re-add only
 * once that is fixed AND the vendor is actually seeded.
 */
const SOURCE_OPTIONS = [
  { value: "", label: "All sources" },
  { value: "apple_watch", label: "Apple Watch Health Monitoring" },
  { value: "pontosense", label: "Pontosense Silver Shield" },
  { value: "xandar_kardian", label: "Xandar Kardian" },
  { value: "boundarycare", label: "BoundaryCare Seizure Monitoring" },
];

// #490 gap 2: the LABELS say what the staff action is; the VALUES stay the
// API's `acknowledged` booleans. Renaming only the column header would have
// left the ambiguous word in the control a caregiver actually operates.
const ACK_OPTIONS = [
  { value: "", label: "All" },
  { value: "true", label: "Cleared" },
  { value: "false", label: "Uncleared" },
];

const EMPTY_FILTERS = {
  from: "",
  to: "",
  vendor: "",
  severity: "",
  eventType: "",
  acknowledged: "",
};

/**
 * #573 — default the list to the last 7 days.
 *
 * With no default range the section fetched the client's ENTIRE alert history
 * on every open — for an active sensor that is hundreds of rows, which buries
 * the recent alerts the caregiver actually came to check. 7 days covers the
 * "what happened this week" question; the presets and date filters keep the
 * full audit history one click away, and the CSV export honours whatever range
 * is applied.
 */
export const DEFAULT_RANGE_DAYS = 7;
const DEFAULT_RANGE_KEY = "7d";

// `days: null` = no lower bound (the pre-#573 behaviour, kept reachable).
export const RANGE_PRESETS = [
  { key: "7d", days: 7, label: "Last 7 days" },
  { key: "30d", days: 30, label: "Last 30 days" },
  { key: "90d", days: 90, label: "Last 90 days" },
  { key: "all", days: null, label: "All history" },
];

/**
 * YYYY-MM-DD for `daysBack` days before today on the CLIENT's calendar — the
 * same calendar the day-bound builders (#494) interpret the date inputs in.
 * Computing "today" in the browser's zone instead would make the default range
 * off by a day for part of every day whenever caregiver and client zones
 * differ.
 */
export const clientDaysAgoYmd = (daysBack, rawZone, now = new Date()) =>
  moment
    .tz(now, resolveClientDisplayZone(rawZone))
    .subtract(daysBack, "days")
    .format("YYYY-MM-DD");

// "Last 7 days" inclusive of today ⇒ from = today − 6 days.
const defaultFiltersForZone = (rawZone, now = new Date()) => ({
  ...EMPTY_FILTERS,
  from: clientDaysAgoYmd(DEFAULT_RANGE_DAYS - 1, rawZone, now),
});

/**
 * #573 — severity as a colour-coded badge instead of plain table text, so a
 * critical row is findable at a glance in a week of warnings. Unknown
 * severities fall back to neutral grey rather than disappearing.
 */
const SEVERITY_BADGE_COLORS = {
  critical: { backgroundColor: "#FDECEA", color: "#B71C1C", borderColor: "#F5C6C0" },
  warning: { backgroundColor: "#FFF8E1", color: "#8A6D00", borderColor: "#FFE082" },
  info: { backgroundColor: "#E8F0FE", color: "#1A56A8", borderColor: "#C6DAFC" },
};

/**
 * Copilot (PR #576): the caption must state the ACTIVE range, so a filtered
 * list is never mistaken for the full history. Preset in force -> its label;
 * custom dates -> the literal bounds; nothing -> all history.
 */
export const appliedRangeLabelFor = (presetKey, appliedFilters) => {
  const preset = RANGE_PRESETS.find((p) => p.key === presetKey);
  if (preset) return preset.label;
  const { from, to } = appliedFilters || {};
  if (from && to) return `${from} to ${to}`;
  if (from) return `From ${from}`;
  if (to) return `Through ${to}`;
  return "All history";
};

export const severityBadgeStyle = (severity) => {
  const palette =
    SEVERITY_BADGE_COLORS[severity] || {
      backgroundColor: "#F0F0F0",
      color: "#555",
      borderColor: "#DDD",
    };
  return {
    display: "inline-block",
    padding: "2px 8px",
    borderRadius: "999px",
    border: `1px solid ${palette.borderColor}`,
    backgroundColor: palette.backgroundColor,
    color: palette.color,
    fontSize: "0.75rem",
    fontWeight: 600,
    textTransform: "capitalize",
    whiteSpace: "nowrap",
  };
};

/**
 * #490 gap 4 — render the coordinates a fall/SOS already carries.
 *
 * The apple_watch adapter normalises `data.location` into
 * `normalizedData.location`, and the list endpoint excludes only `rawPayload`,
 * so `{ lat, lon, accuracy }` is already on the wire. For an unresponsive fall,
 * "where" is the second question after "what", and the screen was discarding
 * the answer.
 *
 * Accuracy is a claim about precision and is treated as one. Production sent
 * `accuracy: 0` for the Aug 10 fall — that is the device declining to state a
 * radius, NOT a zero-metre fix, so it renders as unknown. Rendering it verbatim
 * would draw a confident pin on a reading that never claimed precision.
 *
 * Returns null when there is nothing plottable, so the caller shows a dash
 * rather than a half-coordinate.
 */
// WCAG AA for normal text needs 4.5:1. The previous #777 measured 4.48:1 on
// white and 3.86:1 on the table's #EEEEEE zebra rows — failing on both, and the
// coordinates are the audit record. #595959 is 7.00:1 and 6.04:1 respectively.
const SECONDARY_TEXT = "#595959";

/**
 * Render an accuracy radius, or say the device did not state one.
 *
 * Zero is reserved for "no radius stated". A genuine positive radius that would
 * round to zero is shown as a bound instead.
 */
const formatAccuracyLabel = (accuracy) => {
  if (!Number.isFinite(accuracy) || accuracy <= 0) return "accuracy unknown";
  const rounded = Math.round(accuracy * 10) / 10;
  return rounded === 0 ? "± <0.1 m" : `± ${rounded} m`;
};

export const formatEventLocation = (normalizedData) => {
  const loc = normalizedData?.location;
  if (!loc) return null;

  const { lat, lon, accuracy, formattedAddress } = loc;
  // Both halves or neither — a lone latitude cannot be plotted, and 0 is a
  // legal coordinate, so test for finite numbers rather than truthiness.
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;

  // #1231: the server resolves a display address on the read path. It is
  // absent whenever geocoding is off, failed, or found nothing — in which case
  // this stays null and the cell shows coordinates alone. There is deliberately
  // no placeholder string: anything rendered here must be a real resolved
  // place, never something a reader could mistake for one.
  const address =
    typeof formattedAddress === "string" && formattedAddress.trim()
      ? formattedAddress.trim()
      : null;

  return {
    address,
    // Display precision only. 5 dp is ~1 m — already finer than any of these
    // devices resolve — while the raw pair printed 15 dp of implied precision
    // that no fix supports. `exactCoords` keeps the recorded value reachable.
    coords: `${lat.toFixed(5)}, ${lon.toFixed(5)}`,
    exactCoords: `${lat}, ${lon}`,
    // A positive radius is a real claim; 0, null and absent are all "the
    // device did not say". Rounded to 0.1 m — a device reporting 9.795014 m is
    // not claiming micrometre precision on its own error bar — with whole
    // numbers left whole.
    //
    // A positive radius below 0.05 m is reported as a BOUND, never as a
    // rounded "± 0 m": zero in this cell means the device stated no radius, and
    // rendering a real sub-decimetre fix as zero would assert the one thing
    // this formatter must never assert — a perfect fix.
    accuracyLabel: formatAccuracyLabel(accuracy),
  };
};

/**
 * Which vendor produced this alert.
 *
 * The section is documented as spanning all vendors, and the list endpoint
 * already joins `vendor: { name, displayName }` onto every row — the table was
 * simply discarding it. Without this, a Pontosense fall, a Xandar Kardian fall
 * and an Apple Watch fall render as three identical rows, and the vendor is
 * recoverable only when the free-text message happens to name it.
 *
 * Prefers `displayName` (the seeded human label) and falls back to the machine
 * `name` so a vendor seeded without a display label still attributes rather
 * than showing a dash. Returns a dash only when there is genuinely no join.
 */
export const formatEventSource = (vendor) => {
  // Whitespace-only is not a label. `||` treats "   " as present, which sent
  // the dash back for a row that had a perfectly good machine `name` — the
  // one case this function exists to avoid.
  const usable = (value) =>
    typeof value === "string" && value.trim() !== "" ? value : null;
  return usable(vendor?.displayName) ?? usable(vendor?.name) ?? "—";
};

/**
 * How often the section re-reads the alert list while the tab is in front.
 *
 * This is the surface a caregiver watches for a new IoT alert, and until now it
 * fetched on mount and on Apply only — so "live" meant "live if you reload".
 * A minute is a compromise: short enough that a fall alert is not sitting
 * unseen for long, long enough that a profile left open all shift does not
 * become a meaningful load on the events endpoint.
 */
export const IOT_HISTORY_POLL_MS = 60000;

/**
 * How long the single-poll lock may be held before it is force-released.
 *
 * `apiRequest` uses a plain fetch with no timeout or abort signal
 * (`src/api/baseApi.js`), so a stalled request can stay pending for the life of
 * the session. Without this watchdog the lock that prevents overlapping polls
 * would never be released, and auto-refresh — the entire point of this
 * component — would be silently dead until the page is reloaded. A stuck
 * request must degrade to "polls overlap occasionally", never to "polling
 * stops".
 */
export const IOT_HISTORY_POLL_LOCK_TIMEOUT_MS = 5 * IOT_HISTORY_POLL_MS;

/**
 * Translate a filter set into the API query, in a given zone.
 *
 * Module-level and pure BECAUSE the caller must be able to pass filters other
 * than the live form's: a client change loads unfiltered, and a timezone change
 * reuses the applied filters with their day bounds recomputed.
 */
export const buildApiParams = (filters, clientTimezone) => ({
  // #494: bounds are days on the CLIENT's calendar, matching the dates the
  // rows are labelled with. UTC bounds would visibly disagree with them.
  ...(filters.from
    ? { from: clientDayStartIso(filters.from, clientTimezone) }
    : {}),
  ...(filters.to ? { to: clientDayEndIso(filters.to, clientTimezone) } : {}),
  // #511: omit the key entirely when unset rather than sending `vendor: ""`.
  // toIotEventQuery would drop a blank today, but relying on that would put
  // this filter's correctness in another module.
  ...(filters.vendor ? { vendor: filters.vendor } : {}),
  ...(filters.severity ? { severity: filters.severity } : {}),
  ...(filters.eventType.trim() ? { eventType: filters.eventType.trim() } : {}),
  ...(filters.acknowledged ? { acknowledged: filters.acknowledged } : {}),
});

const IotEventHistorySection = ({ clientId, clientTimezone }) => {
  const [events, setEvents] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState("");
  // #573: open on the last 7 days, not the whole history.
  const [filters, setFilters] = useState(() =>
    defaultFiltersForZone(clientTimezone),
  );
  // Which range preset produced the current from/to — "" once a date is edited
  // by hand, so the chips never claim a range the inputs contradict.
  const [activePreset, setActivePreset] = useState(DEFAULT_RANGE_KEY);
  // Ref mirror so callbacks with stable deps (poll, Apply) can read the live
  // chip state without rebuilding per keystroke.
  const activePresetRef = useRef(DEFAULT_RANGE_KEY);
  const changeActivePreset = (key) => {
    activePresetRef.current = key;
    setActivePreset(key);
  };
  // The preset in force for the APPLIED params — what the poll re-derives
  // from. Distinct from activePreset, which tracks the (possibly unapplied)
  // form. Copilot (PR #576): a preset collapsed to a fixed date at click time
  // drifts past the client's midnight; keeping the KEY applied lets every
  // fetch regenerate the lower bound on the current client calendar.
  const appliedPresetRef = useRef(DEFAULT_RANGE_KEY);
  // Caption: the range the visible rows were selected under.
  const [appliedRangeLabel, setAppliedRangeLabel] = useState(
    appliedRangeLabelFor(DEFAULT_RANGE_KEY, null),
  );

  // Translate the form filters into the API query. Date inputs are whole-day;
  // widen `to` to end-of-day so a single-day range is inclusive.
  const toApiParams = useCallback(
    () => buildApiParams(filters, clientTimezone),
    [filters, clientTimezone],
  );

  // #494: a client or timezone change starts a new fetch while an earlier one
  // may still be in flight, and responses can land out of order — the stale
  // one would then win and show rows for the previous zone under the new
  // labels. Only the newest request is allowed to write state.
  // Review finding: the sequence guard rejects a SUPERSEDED request, never an
  // UNMOUNTED one — nothing bumps `requestSeq` on unmount. So a request that
  // resolved after the caregiver navigated away passed the check and emitted
  // `load_succeeded` for a panel nobody saw. Under #621 that breadcrumb IS the
  // acceptance signal, so a false one is worse than a missing one.
  const isMounted = useRef(true);
  const requestSeq = useRef(0);

  // Loading ownership, tracked SEPARATELY from requestSeq. A silent poll bumps
  // the shared sequence, so an in-flight explicit load would fail its own
  // `seq === requestSeq.current` check in `finally` and never clear `loading` —
  // leaving Apply disabled as "Loading…" for good. The two concerns are
  // genuinely different: requestSeq decides who may WRITE results, this decides
  // who owns the spinner.
  const foregroundSeq = useRef(0);
  const foregroundInFlight = useRef(false);
  // One background refresh at a time. The interval fires on a schedule whether
  // or not the previous poll came back, and returning to the tab starts another
  // — requestSeq stops a stale response from WRITING, but nothing stops slow
  // requests accumulating, and this path has no timeout or abort signal.
  const pollInFlight = useRef(false);

  // The params of the last EXPLICIT load (mount, client/zone change, Apply).
  // Filters update on every keystroke but are only meant to take effect on
  // Apply, so a poll reading live `filters` would filter the table out from
  // under someone mid-edit. The poll reads this instead.
  const appliedParamsRef = useRef({});
  // The RAW filters behind those params, so a timezone change can recompute the
  // day bounds instead of reusing bounds built in the old zone. Starts at the
  // 7-day default (#573) because the mount lifecycle load reads THIS ref, not
  // the form state (see loadForLifecycle).
  const appliedFiltersRef = useRef(null);
  if (appliedFiltersRef.current === null) {
    appliedFiltersRef.current = defaultFiltersForZone(clientTimezone);
  }

  // The teardown for the `isMounted` ref above. Without it the ref is a
  // constant `true` and the guard it feeds is dead code that reads as a guard —
  // the failure mode a mutation test exists to catch.
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  /**
   * `silent` is what makes polling tolerable rather than annoying:
   *
   *  - no loading state, so the Apply button does not flicker to "Loading…"
   *    once a minute under someone who is reading the table
   *  - a failure leaves the rows alone. Blanking a caregiver's alert list on
   *    one transient network error is worse than showing rows a minute old,
   *    and the foreground path still reports errors normally.
   */
  const runFetch = useCallback(
    async (params, { silent = false } = {}) => {
      if (!clientId) return;
      const seq = ++requestSeq.current;
      const fgSeq = silent ? null : ++foregroundSeq.current;
      if (!silent) {
        foregroundInFlight.current = true;
        setLoading(true);
        setError("");
      }
      try {
        const data = await fetchClientIotEvents(clientId, params);
        if (seq !== requestSeq.current) return;
        setEvents(data?.events || []);
        setTotal(typeof data?.total === "number" ? data.total : 0);
        // A poll that succeeds after a failed foreground load clears the stale
        // banner — otherwise the error outlives the condition.
        if (silent) setError("");
        if (isMounted.current) {
          panelLoadBreadcrumb("iot_events", true, {
            clientId,
            rowCount: (data?.events || []).length,
          });
        }
      } catch (err) {
        if (seq !== requestSeq.current) return;
        if (silent) return;
        // Same reason as the success breadcrumb above, and the review was right
        // to ask for both: the sequence guard rejects a SUPERSEDED request, not
        // an unmounted one. This is a LOAD, so an unmounted panel means the
        // caregiver was shown nothing and the report is noise. (An ACTION is
        // different -- see PontosenseStaffSection, where the failure is real
        // whether or not anyone is looking and only the UI write is guarded.)
        if (!isMounted.current) return;
        // #621: past the seq and `silent` guards, so this is a failure the
        // caregiver is actually shown. A silent background poll returns
        // above and is deliberately NOT reported — see the suite docblock
        // for why that is a separate, rate-limited concern.
        capturePanelFailure(err, {
          panel: "iot-event-history",
          surfaceKey: "iot_events",
          clientId,
        });
        setError(err.message || "Failed to load IoT alert history");
        setEvents([]);
      } finally {
        if (!silent && fgSeq === foregroundSeq.current) {
          foregroundInFlight.current = false;
          setLoading(false);
        }
      }
    },
    [clientId],
  );

  const refresh = useCallback(async () => {
    const params = toApiParams();
    appliedFiltersRef.current = filters;
    appliedParamsRef.current = params;
    // Apply commits whatever the chips currently claim ("" = custom range).
    appliedPresetRef.current = activePresetRef.current;
    setAppliedRangeLabel(
      appliedRangeLabelFor(activePresetRef.current, filters),
    );
    await runFetch(params, { silent: false });
  }, [runFetch, toApiParams, filters]);

  /**
   * Lifecycle loads (client change, timezone change) must NOT read the live
   * form. `setFilters(EMPTY_FILTERS)` on a client change has not committed when
   * this runs, so reading `filters` here captures the PREVIOUS client's
   * filters — and storing them means every later poll keeps using filters the
   * user cannot see. A timezone change is the opposite case: the applied
   * filters still stand, but their day bounds must be recomputed in the new
   * zone.
   */
  const loadForLifecycle = useCallback(
    async (nextFilters, presetKey) => {
      const params = buildApiParams(nextFilters, clientTimezone);
      appliedFiltersRef.current = nextFilters;
      appliedParamsRef.current = params;
      if (presetKey !== undefined) appliedPresetRef.current = presetKey;
      setAppliedRangeLabel(
        appliedRangeLabelFor(appliedPresetRef.current, nextFilters),
      );
      await runFetch(params, { silent: false });
    },
    [runFetch, clientTimezone],
  );

  // Depends only on `runFetch` (and so only on clientId), NOT on `refresh` —
  // whose identity changes on every keystroke in the filter form. An interval
  // keyed on `refresh` would be torn down and rebuilt as someone typed.
  /**
   * Copilot (PR #576): while a relative preset is applied, its lower bound is
   * a function of "today" on the client's calendar — regenerate it whenever
   * the applied params are about to be used (each poll, and a timezone
   * change), so "Last 7 days" never silently becomes 8 past midnight. Manual
   * ranges stay literal. Also syncs the visible From input when the form
   * still reflects the applied preset (never over someone's mid-edit).
   */
  const refreshAppliedPresetBounds = useCallback(() => {
    const preset = RANGE_PRESETS.find(
      (p) => p.key === appliedPresetRef.current && p.days !== null,
    );
    if (!preset) return;
    const from = clientDaysAgoYmd(preset.days - 1, clientTimezone);
    if (appliedFiltersRef.current.from === from) return;
    const next = { ...appliedFiltersRef.current, from, to: "" };
    appliedFiltersRef.current = next;
    appliedParamsRef.current = buildApiParams(next, clientTimezone);
    if (activePresetRef.current === appliedPresetRef.current) {
      setFilters((prev) => ({ ...prev, from, to: "" }));
    }
  }, [clientTimezone]);

  const pollRefresh = useCallback(() => {
    // Skip entirely while an explicit load is in flight: the user is waiting on
    // a result they asked for, and a poll can only race it.
    if (foregroundInFlight.current || pollInFlight.current) return undefined;
    refreshAppliedPresetBounds();
    pollInFlight.current = true;
    const watchdog = setTimeout(() => {
      pollInFlight.current = false;
    }, IOT_HISTORY_POLL_LOCK_TIMEOUT_MS);
    return runFetch(appliedParamsRef.current, { silent: true }).finally(() => {
      clearTimeout(watchdog);
      pollInFlight.current = false;
    });
  }, [runFetch, refreshAppliedPresetBounds]);

  // Load once on mount / when the client changes. Filter changes are applied
  // explicitly via the Apply button so typing a type doesn't fire per-keystroke.
  // A client change resets to the 7-day default (#573), not to unfiltered.
  useEffect(() => {
    setFilters(defaultFiltersForZone(clientTimezone));
    changeActivePreset(DEFAULT_RANGE_KEY);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  // #494: also re-fetch when the client's zone changes (an edit + roster
  // refetch mid-session). The bounds are built from that zone, so without this
  // the table still shows rows fetched under the OLD zone while Export would
  // send the new bounds — the same display/fetch disagreement the zoned bounds
  // were added to remove. `filters` is deliberately NOT a dependency: applying
  // a filter stays an explicit button press.
  const prevClientIdRef = useRef(clientId);
  useEffect(() => {
    // Rows on screen were selected under the OLD zone's day bounds while the
    // formatter switches immediately, so leaving them up shows out-of-range
    // events under the new zone's label. Clear first, then refetch.
    setEvents([]);
    setTotal(0);
    const clientChanged = prevClientIdRef.current !== clientId;
    prevClientIdRef.current = clientId;
    // A client change loads the 7-DAY DEFAULT (#573) — the sibling effect's
    // setFilters(...) has not committed yet, so reading the live form here
    // would capture the previous client's filters and every later poll would
    // keep using them. A timezone change keeps the applied filters and
    // rebuilds their bounds in the new zone.
    if (clientChanged) {
      loadForLifecycle(defaultFiltersForZone(clientTimezone), DEFAULT_RANGE_KEY);
    } else {
      // Timezone change: a relative preset regenerates its bound on the NEW
      // calendar (Copilot, PR #576); a custom range keeps its literal dates,
      // with the day bounds rebuilt in the new zone by buildApiParams.
      refreshAppliedPresetBounds();
      loadForLifecycle(appliedFiltersRef.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, clientTimezone]);

  // Poll while the tab is in front. A backgrounded profile left open all day
  // would otherwise keep asking for rows nobody is looking at, and on return
  // the reader wants the current list immediately rather than at the next tick.
  useEffect(() => {
    if (!clientId) return undefined;

    let timer = null;
    const start = () => {
      if (timer === null) timer = setInterval(pollRefresh, IOT_HISTORY_POLL_MS);
    };
    const stop = () => {
      if (timer !== null) {
        clearInterval(timer);
        timer = null;
      }
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        stop();
      } else {
        pollRefresh();
        start();
      }
    };

    if (document.visibilityState !== "hidden") start();
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [clientId, pollRefresh]);

  const setFilter = (key, value) =>
    setFilters((prev) => ({ ...prev, [key]: value }));

  // Hand-editing a date makes the range custom — the chips must not stay
  // pressed claiming a window the inputs no longer describe.
  const setRangeFilter = (key, value) => {
    changeActivePreset("");
    setFilter(key, value);
  };

  /**
   * #573 — one-click range. Applies immediately (no separate Apply press):
   * the preset IS the apply action, and routing through loadForLifecycle
   * updates the applied refs so the minute-poll keeps the chosen window.
   * `to` clears because every preset means "through today".
   */
  const applyPreset = (preset) => {
    const next = {
      ...filters,
      from:
        preset.days !== null
          ? clientDaysAgoYmd(preset.days - 1, clientTimezone)
          : "",
      to: "",
    };
    changeActivePreset(preset.key);
    setFilters(next);
    loadForLifecycle(next, preset.key);
  };

  const handleExport = async () => {
    if (exporting || !clientId) return;
    setExporting(true);
    try {
      await exportClientIotEventsCsv(clientId, toApiParams());
      toast.success("IoT alert history exported");
    } catch (err) {
      toast.error(err.message || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  const panelStyle = {
    marginBottom: "24px",
    backgroundColor: "#F5F5F5",
    color: "#333",
    padding: "16px",
    borderRadius: "8px",
    border: "1px solid #E0E0E0",
    borderLeft: "4px solid #607D8B",
  };

  const inputStyle = { padding: "6px", fontSize: "0.85rem" };

  return (
    <div
      className="client-profile-section"
      style={panelStyle}
      data-testid="iot-history-section"
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "12px",
        }}
      >
        <h2 style={{ fontSize: "1.1rem", margin: 0, fontWeight: 800 }}>
          IoT Alert History
        </h2>
        <button
          type="button"
          data-testid="iot-history-export"
          onClick={handleExport}
          disabled={exporting}
        >
          {exporting ? "Exporting…" : "Export CSV"}
        </button>
      </div>

      <p style={{ fontSize: "0.9rem", color: "#555", marginTop: 0 }}>
        Cleared and uncleared sensor alerts across all vendors, kept for audit.
        Shows the last {DEFAULT_RANGE_DAYS} days by default — use the range
        buttons or date filters for older alerts. Filter and export for QA /
        SOP2 review.
      </p>

      {/* #490 gap 2: "Cleared" is a staff action and was being read as proof a
          caregiver had been told. Those are different facts, and conflating
          them is what made five days of lost SOS events look normal. The
          delivery fact is not reachable from this endpoint — alerts_delivered_at
          lives on safety_events, and this list joins only the vendor — so the
          honest thing is to name the gap rather than let it be inferred. */}
      <p
        data-testid="iot-history-delivery-note"
        style={{
          fontSize: "0.8rem",
          color: "#6B5200",
          backgroundColor: "#FFF8E1",
          border: "1px solid #FFE082",
          borderRadius: "6px",
          padding: "8px 10px",
          marginTop: 0,
        }}
      >
        Clearing is a staff action. This view <strong>does not show whether a
        caregiver was notified</strong> — an alert can be cleared here without
        any notification having been delivered, and an event rejected before it
        was stored never appears at all.
      </p>

      <div
        style={{
          display: "flex",
          gap: "6px",
          marginBottom: "10px",
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <span style={{ fontSize: "0.8rem", color: "#555" }}>Range:</span>
        {RANGE_PRESETS.map((preset) => {
          const active = activePreset === preset.key;
          return (
            <button
              key={preset.key}
              type="button"
              data-testid={`iot-history-range-${preset.key}`}
              aria-pressed={active}
              onClick={() => applyPreset(preset)}
              disabled={loading}
              style={{
                padding: "4px 10px",
                fontSize: "0.8rem",
                borderRadius: "999px",
                border: active ? "1px solid #455A64" : "1px solid #CFD8DC",
                backgroundColor: active ? "#455A64" : "#FFFFFF",
                color: active ? "#FFFFFF" : "#455A64",
                cursor: "pointer",
              }}
            >
              {preset.label}
            </button>
          );
        })}
      </div>

      <div
        style={{
          display: "flex",
          gap: "8px",
          marginBottom: "12px",
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <label style={{ fontSize: "0.8rem", color: "#555" }}>
          From{" "}
          <input
            type="date"
            data-testid="iot-history-from"
            value={filters.from}
            onChange={(e) => setRangeFilter("from", e.target.value)}
            style={inputStyle}
          />
        </label>
        <label style={{ fontSize: "0.8rem", color: "#555" }}>
          To{" "}
          <input
            type="date"
            data-testid="iot-history-to"
            value={filters.to}
            onChange={(e) => setRangeFilter("to", e.target.value)}
            style={inputStyle}
          />
        </label>
        <select
          aria-label="Filter by source"
          data-testid="iot-history-vendor"
          value={filters.vendor}
          onChange={(e) => setFilter("vendor", e.target.value)}
          style={inputStyle}
        >
          {SOURCE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <select
          aria-label="Filter by severity"
          data-testid="iot-history-severity"
          value={filters.severity}
          onChange={(e) => setFilter("severity", e.target.value)}
          style={inputStyle}
        >
          {SEVERITY_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <input
          type="text"
          aria-label="Filter by event type"
          data-testid="iot-history-eventtype"
          placeholder="Event type"
          value={filters.eventType}
          onChange={(e) => setFilter("eventType", e.target.value)}
          style={{ ...inputStyle, minWidth: "150px" }}
        />
        <select
          aria-label="Filter by cleared status"
          data-testid="iot-history-acknowledged"
          value={filters.acknowledged}
          onChange={(e) => setFilter("acknowledged", e.target.value)}
          style={inputStyle}
        >
          {ACK_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <button
          type="button"
          data-testid="iot-history-apply"
          onClick={refresh}
          disabled={loading}
        >
          {loading ? "Loading…" : "Apply"}
        </button>
      </div>

      {error ? (
        <p role="alert" style={{ color: "#B71C1C", fontSize: "0.85rem" }}>
          {error}
        </p>
      ) : null}

      <p
        data-testid="iot-history-range-note"
        style={{ fontSize: "0.8rem", color: "#555", marginTop: 0 }}
      >
        Showing: <strong>{appliedRangeLabel}</strong>
      </p>

      {loading && events.length === 0 ? (
        <p>Loading alert history…</p>
      ) : events.length === 0 ? (
        <p data-testid="iot-history-empty">
          No IoT alerts match these filters.
        </p>
      ) : (
        <>
          {/* The section renders in the profile's left column, so a 7-column
              audit table can exceed the available width on a narrow viewport.
              Scroll the table rather than the page. */}
          {/* Focusable + labelled: a plain overflow container is not
              keyboard-scrollable, so a keyboard-only or AT user cannot reach
              the columns that scroll off (Source, Location, Cleared by staff). */}
          <div
            data-testid="iot-history-scroll"
            role="region"
            aria-label="IoT alert history table, scrolls horizontally"
            tabIndex={0}
            style={{ overflowX: "auto" }}
          >
            {/* minWidth is what makes the scroll region real: at width:100%
                the table compresses to the container instead of overflowing,
                so overflowX never engages and the tab stop above would focus
                an element that cannot be scrolled. */}
            <table
              style={{ width: "100%", minWidth: "640px", fontSize: "0.85rem" }}
            >
              <thead>
                <tr>
                  <th align="left">Time</th>
                  <th align="left">Source</th>
                  <th align="left">Type</th>
                  <th align="left">Severity</th>
                  <th align="left">Message</th>
                  <th align="left">Location</th>
                  <th align="left">Cleared by staff</th>
                </tr>
              </thead>
              <tbody>
                {events.map((ev, idx) => {
                  const place = formatEventLocation(ev.normalizedData);
                  return (
                  <tr
                    key={ev.id}
                    data-testid={`iot-history-row-${ev.id}`}
                    // #573: zebra rows — a week of alerts is still a long
                    // table, and unstriped rows are hard to track across
                    // seven columns.
                    style={{
                      backgroundColor: idx % 2 === 1 ? "#EEEEEE" : "transparent",
                    }}
                  >
                    <td>{formatClientZonedTime(ev.eventAt, clientTimezone)}</td>
                    <td data-testid={`iot-history-source-${ev.id}`}>
                      {formatEventSource(ev.vendor)}
                    </td>
                    <td>{ev.eventType}</td>
                    <td>
                      <span
                        data-testid={`iot-history-severity-${ev.id}`}
                        style={severityBadgeStyle(ev.severity)}
                      >
                        {ev.severity}
                      </span>
                    </td>
                    <td>{ev.message || "—"}</td>
                    <td data-testid={`iot-history-location-${ev.id}`}>
                      {place ? (
                        // #1231: address leads, coordinates stay beneath it as
                        // the record. When no address resolved, the coordinate
                        // line is the whole cell — never a placeholder word.
                        <span title={place.exactCoords}>
                          {place.address && (
                            <span
                              data-testid={`iot-history-address-${ev.id}`}
                              style={{ display: "block" }}
                            >
                              {place.address}
                            </span>
                          )}
                          <span
                            style={{
                              whiteSpace: "nowrap",
                              color: place.address ? SECONDARY_TEXT : undefined,
                              fontSize: place.address ? "0.75rem" : undefined,
                            }}
                          >
                            {place.coords}
                            <span
                              style={{
                                color: SECONDARY_TEXT,
                                fontSize: "0.75rem",
                                marginLeft: "6px",
                              }}
                            >
                              {place.accuracyLabel}
                            </span>
                          </span>
                        </span>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td>
                      {ev.acknowledgedAt
                        ? `Yes — ${formatClientZonedTime(
                            ev.acknowledgedAt,
                            clientTimezone,
                          )}`
                        : "No"}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p style={{ fontSize: "0.8rem", color: SECONDARY_TEXT, marginTop: "8px" }}>
            Showing {events.length} of {total} alert(s).
          </p>
        </>
      )}
    </div>
  );
};

export default IotEventHistorySection;
